import { MONEY_UNIT } from '@web-platform/shared-universal-constants/src/money';
import { SORT_TYPES } from '@web-platform/shared-universal-constants/src/sort';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { PRICE_RANGE_INDICATOR } from '@web-platform/shared-universal-types/src/PriceBands';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { formatMoney } from '@web-platform/shared-universal-utils-frontend/src/helpers/formatMoney';
import { getPerPersonPrice } from '@web-platform/shared-universal-utils-frontend/src/helpers/getPerPersonPrice';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import type { Money } from '@web-platform/shared-universal-constants/src/money';
import type {
  PriceBandsData,
  PriceBandsProps,
  PriceRangeIndicator,
  RelativePercentageType,
} from '@web-platform/shared-universal-types/src/PriceBands';
import type {
  PriceBandsState,
  BuildPriceBandsData,
} from '@web-platform/shared-universal-types/src/redux/state/priceBands/priceBands';

export const roundUp = (value: number): number => {
  // round to nearest hundred thousand if over a million
  if (value >= 1_000_000) {
    return Math.ceil(value / 100_000) * 100_000;
  }

  // round to nearest hundred if over 10 thousand
  if (value >= 10_000) {
    return Math.ceil(value / 100) * 100;
  }
  return Math.ceil(value);
};

export const calcPriceIndicatorValue = (
  priceMarker: Money,
  higherThresholdPrice: Money,
  lowerThresholdPrice: Money,
): PriceRangeIndicator | undefined => {
  switch (true) {
    // Price marker is less than the lower threshold, so price is lower than usual
    case Number(priceMarker.amount) < Number(lowerThresholdPrice.amount):
      return PRICE_RANGE_INDICATOR.LOWER;
    // Price marker is larger than the higher threshold, so price is higher than usual
    case Number(priceMarker.amount) > Number(higherThresholdPrice.amount):
      return PRICE_RANGE_INDICATOR.HIGHER;
    // Else price marker sits in between the two thresholds, so price is as expected
    case Number(priceMarker.amount) < Number(higherThresholdPrice.amount) &&
      Number(priceMarker.amount) > Number(lowerThresholdPrice.amount):
      return PRICE_RANGE_INDICATOR.EXPECTED;
    default:
      return undefined;
  }
};

export const calcPriceDifference = (priceMarker: Money, targetThreshold: Money): Money => {
  const roundedDiff = Math.ceil(
    Math.abs(Number(priceMarker.amount) - Number(targetThreshold.amount)),
  );

  if (Number.isNaN(roundedDiff)) {
    throw new Error('Error calculating price difference: result is NaN');
  }

  return {
    amount: roundedDiff.toString(),
    currency_code: priceMarker.currency_code,
    unit: priceMarker.unit,
  };
};

export const priceBandsSelectorMapper = (
  priceBands: PriceBandsState,
  priceMarker: Money,
): PriceBandsProps | null => {
  if (
    !priceBands.percentileValueLow ||
    !priceBands.percentileValueHigh ||
    !priceBands.pHigh ||
    !priceBands.pLow ||
    !priceBands.pOutlierHigh ||
    !priceBands.pOutlierLow
  ) {
    logWarn({
      component: 'PriceBands',
      message: `Error with PriceBands: one or more price band value(s) are undefined`,
    });
    return null;
  }
  const priceMarkerPercentage = getRelativePercentageBetweenBounds({
    percentileValueHigh: priceBands.percentileValueHigh,
    percentileValueLow: priceBands.percentileValueLow,
    pHigh: priceBands.pHigh,
    pLow: priceBands.pLow,
    pOutlierHigh: priceBands.pOutlierHigh,
    pOutlierLow: priceBands.pOutlierLow,
    priceMarker,
  });

  const priceRangeIndicator =
    calcPriceIndicatorValue(priceMarker, priceBands.pHigh, priceBands.pLow) ??
    PRICE_RANGE_INDICATOR.EXPECTED;

  const priceDifference = calcPriceDifference(
    priceMarker,
    priceRangeIndicator === PRICE_RANGE_INDICATOR.HIGHER ? priceBands.pHigh : priceBands.pLow,
  );

  return {
    pOutlierLow: priceBands.pOutlierLow,
    pLow: priceBands.pLow,
    pHigh: priceBands.pHigh,
    pOutlierHigh: priceBands.pOutlierHigh,
    percentileValueLow: priceBands.percentileValueLow,
    percentileValueHigh: priceBands.percentileValueHigh,
    // Enriched/derived data values
    priceMarker,
    priceMarkerPercentage,
    priceRangeIndicator,
    priceDifference,
  };
};

export const getRelativePercentageBetweenBounds = ({
  pHigh,
  pLow,
  pOutlierHigh,
  pOutlierLow,
  percentileValueHigh,
  percentileValueLow,
  priceMarker,
}: RelativePercentageType) => {
  if (!priceMarker || !pHigh || !pLow || !pOutlierHigh || !pOutlierLow) {
    return 0;
  }

  const ceilObject = {
    indicative: Math.ceil(formatMoney(priceMarker, false)),
    outlierLow: Math.ceil(formatMoney(pOutlierLow, false)),
    outlierHigh: Math.ceil(formatMoney(pOutlierHigh, false)),
    priceHigh: Math.ceil(formatMoney(pHigh, false)),
    priceLow: Math.ceil(formatMoney(pLow, false)),
  };

  if (
    ceilObject.indicative === ceilObject.outlierLow &&
    ceilObject.outlierLow === ceilObject.outlierHigh
  ) {
    throw new Error('Division by zero');
  }

  if (ceilObject.indicative <= ceilObject.outlierLow) return 0;
  if (ceilObject.indicative >= ceilObject.outlierHigh) return 100;

  if (
    ceilObject.indicative <= ceilObject.priceHigh &&
    ceilObject.indicative >= ceilObject.priceLow
  ) {
    const priceRange = ceilObject.priceHigh - ceilObject.priceLow;
    const percentRange = percentileValueHigh - percentileValueLow;
    const positionInRange = (ceilObject.indicative - ceilObject.priceLow) / priceRange;
    return Math.ceil(percentileValueLow + positionInRange * percentRange);
  }

  if (ceilObject.indicative < ceilObject.priceLow) {
    const lowRange = ceilObject.priceLow - ceilObject.outlierLow;
    const positionInLowRange = (ceilObject.indicative - ceilObject.outlierLow) / lowRange;
    return Math.ceil(positionInLowRange * percentileValueLow);
  }

  const highRange = ceilObject.outlierHigh - ceilObject.priceHigh;
  const positionInHighRange = (ceilObject.indicative - ceilObject.priceHigh) / highRange;
  return Math.ceil(percentileValueHigh + positionInHighRange * (100 - percentileValueHigh));
};

export const hasUndefinedValues = (object: any): boolean =>
  Object.values(object).some((value) => value === undefined);

const noPriceBandsDataExists = (state: PriceBandsState): boolean =>
  !state.isLoading && hasUndefinedValues(state);

const getPriceMarker = (selectedTab: any, currency: string, passengers: number): Money | null => {
  if (!selectedTab?.price) {
    return null;
  }

  // Work out price marker based on selected FQS tab
  return {
    amount: roundUp(getPerPersonPrice(selectedTab.price, passengers)).toString(),
    currency_code: currency,
    unit: MONEY_UNIT.UNIT_WHOLE,
  };
};

export const buildPriceBandsData = ({
  currency,
  fpsCompleted,
  fqsTabs,
  hasReachedThresholdPercentage,
  passengers,
  priceBands,
  selectedSort,
}: BuildPriceBandsData): PriceBandsData => {
  const priceBandsData = !noPriceBandsDataExists(priceBands);
  // Find the tab that matches the selected sort option if not found then default to 'BEST' tab
  const selectedTab =
    fqsTabs.find((tab) => tab?.option === selectedSort) ??
    fqsTabs.find((tab) => tab?.option === SORT_TYPES.BEST);
  const priceMarker = getPriceMarker(selectedTab, currency, passengers);

  const isStaticFilteringVariant = getFeatureDecision(
    FEATURE_DECISION_KEYS.ENABLE_PRICE_BANDS_STATIC_FILTERING,
  );

  // True when any price bands variant uses custom search completion (not fps-based)
  const isPriceBandsVariantEnabled =
    getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_PRICE_BANDS_MESSAGE_LOADING_STATE) ||
    getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_PRICE_BANDS_EMPTY_LOADING_STATE) ||
    getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_PRICE_BANDS_DYNAMIC_FILTERING) ||
    isStaticFilteringVariant;

  // Use carrier threshold for dynamic variant, no threshold for static variant.
  // Otherwise use fps completion.
  const searchCompleted = isPriceBandsVariantEnabled
    ? isStaticFilteringVariant || hasReachedThresholdPercentage
    : fpsCompleted;

  // Log warning if search completed but price marker still cannot be determined
  const resultsAvailable =
    searchCompleted && fqsTabs.length > 0 && fqsTabs.every((tab) => tab !== null);
  if (resultsAvailable && !priceMarker) {
    const fqsTabsString = fqsTabs.map((tab) => tab?.option).join(', ');
    logWarn({
      component: 'PriceBands',
      message: `Error with PriceBands: price marker could not be determined. FQS Tabs: [${fqsTabsString}]. Selected Sort: ${selectedSort}. Selected Tab Price: ${selectedTab?.price}.`,
    });
  }

  const priceBandsDataExists = !!priceMarker && priceBandsData;

  // Show loading state while price bands are loading,
  // we have price bands data AND search not yet completed, OR
  // the static variant is waiting for fqsTabs to arrive (required by priceBandsSelectorMapper)
  const priceBandsLoading =
    priceBands.isLoading ||
    (priceBandsData && !searchCompleted) ||
    (isStaticFilteringVariant && priceBandsData && fqsTabs.length === 0);

  return {
    isLoading: priceBandsLoading,
    isError: priceBands.isError,
    priceBands:
      priceBandsLoading || !priceBandsDataExists
        ? null
        : priceBandsSelectorMapper(priceBands, priceMarker),
  };
};
